import { Button, Popconfirm, Tooltip, Typography } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  RightOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import d from 'dayjs';
import { toast } from 'react-hot-toast';
import api from 'src/api';
import AddCalendarModal from './add-calendar-popup';
import CopyHotelCalendar from './hotel-modal-calendar-copy';
import Spinner from './spinner';

/**
 * Side panel (Airbnb "Link calendars" style): lists every iCal feed linked to
 * the property with its last sync time, and lets the user refresh one feed,
 * refresh all of them, edit / remove a feed, link a new one or export the
 * Loger calendar. Sync also runs automatically every hour on the server.
 */
function CalendarSyncPanel({ propertyId, property, rooms = [], isMobile }) {
  const queryClient = useQueryClient();
  const propertyType = property?.propertyType;
  const isHotel = propertyType === 'hotel';

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [isCopyOpen, setIsCopyOpen] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);

  const { data: calendars = [], isFetching } = useQuery({
    queryKey: ['calendars', propertyId],
    enabled: !!propertyId,
    queryFn: async () => {
      const res = await api.get(`/calendar-sync/${propertyId}`);
      return res?.data?.calendars || [];
    }
  });

  const refreshAfterSync = () => {
    queryClient.invalidateQueries(['calendars', propertyId]);
    queryClient.invalidateQueries(['calendar-entries', propertyId]);
  };

  const { mutate: sync, isPending: isSyncing } = useMutation({
    mutationFn: async calendarId => {
      setRefreshingId(calendarId || 'all');
      const res = await api.post(
        `/calendar-sync/sync/${propertyId}${calendarId ? `/${calendarId}` : ''}`
      );
      return res?.data;
    },
    onSuccess: data => {
      refreshAfterSync();
      if (data?.errors?.length) {
        toast.error(
          `Could not fetch: ${data.errors.map(e => e.name).join(', ')}`
        );
      } else {
        toast.success('Calendar synced');
      }
    },
    onError: err => {
      toast.error(err?.response?.data?.message || 'Calendar sync failed');
    },
    onSettled: () => setRefreshingId(null)
  });

  const { mutate: remove, isPending: isDeleting } = useMutation({
    mutationFn: async calendarId => {
      const res = await api.delete(`/calendar-sync/${calendarId}`);
      return res?.data;
    },
    onSuccess: () => {
      // Blocked dates from the removed feed are dropped on the next sync.
      sync();
      toast.success('Calendar removed');
    },
    onError: err => {
      toast.error(err?.response?.data?.message || 'Could not remove calendar');
    }
  });

  function copyExportLink() {
    if (isHotel) {
      setIsCopyOpen(true);
      return;
    }
    const link = `${process.env.REACT_APP_API_URL}/calendar/export-ics/${propertyId}`;
    window.navigator.clipboard.writeText(link);
    toast.success('Calendar link copied to clipboard');
  }

  const roomLabel = roomTypeId => {
    const room = rooms.find(r => r.roomTypeId === roomTypeId);
    return room?.displayName || room?.roomName;
  };

  const cardStyle = {
    border: '1px solid #dddddd',
    borderRadius: '12px',
    background: '#fff',
    overflow: 'hidden'
  };
  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: isMobile ? '10px 12px' : '12px 16px',
    borderTop: '1px solid #ebebeb'
  };
  const actionBtnStyle = {
    padding: 0,
    height: 'auto',
    color: '#222',
    fontWeight: 500
  };

  return (
    <>
      {propertyType && (
        <>
          <AddCalendarModal
            {...{
              propertyId,
              isOpen: isAddOpen,
              close: () => setIsAddOpen(false),
              rooms,
              isFetching: false,
              propertyType,
              isMobile
            }}
          />
          <AddCalendarModal
            {...{
              propertyId,
              selectedCalendar,
              isOpen: !!selectedCalendar,
              close: () => setSelectedCalendar(null),
              rooms,
              isFetching: false,
              propertyType,
              isMobile
            }}
          />
          <CopyHotelCalendar
            {...{
              propertyId,
              isOpen: isCopyOpen,
              close: () => setIsCopyOpen(false),
              rooms,
              isFetching: false,
              propertyType,
              isMobile
            }}
          />
        </>
      )}

      <div
        style={{
          width: '100%',
          border: '1px solid #8d9197',
          background: '#fff',
          borderRadius: '10px',
          padding: isMobile ? '12px' : '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '10px' : '12px'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <div>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Link calendars
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              Synced automatically every hour
            </Typography.Text>
          </div>
          {!!calendars.length && (
            <Tooltip title="Refresh all calendars now">
              <Button
                size="small"
                icon={<SyncOutlined spin={refreshingId === 'all'} />}
                loading={refreshingId === 'all'}
                disabled={isSyncing}
                onClick={() => sync()}
              >
                Refresh all
              </Button>
            </Tooltip>
          )}
        </div>

        {isFetching && !calendars.length ? (
          <Spinner />
        ) : !calendars.length ? (
          <Typography.Text type="secondary">
            No calendars linked yet. Link Booking.com, Airbnb or any iCal feed
            to block dates booked elsewhere.
          </Typography.Text>
        ) : (
          calendars.map(calendar => {
            const isThisRefreshing = refreshingId === calendar._id;
            return (
              <div key={calendar._id} style={cardStyle}>
                <div style={{ padding: isMobile ? '10px 12px' : '12px 16px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Typography.Text strong ellipsis style={{ fontSize: '15px' }}>
                      {calendar.name}
                    </Typography.Text>
                    <Popconfirm
                      title="Remove this calendar?"
                      description="Dates blocked by this feed will be released."
                      okText="Remove"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => remove(calendar._id)}
                    >
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        disabled={isDeleting || isSyncing}
                      />
                    </Popconfirm>
                  </div>
                  {isHotel && calendar.roomTypeId && (
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: '12px', display: 'block' }}
                    >
                      {roomLabel(calendar.roomTypeId) || calendar.roomTypeId}
                      {calendar.roomCount ? ` · ${calendar.roomCount} room(s)` : ''}
                    </Typography.Text>
                  )}
                  <Typography.Text
                    type={calendar.lastSyncError ? 'danger' : 'secondary'}
                    style={{ fontSize: '12px', display: 'block' }}
                  >
                    {calendar.lastSyncError
                      ? `Last sync failed: ${calendar.lastSyncError}`
                      : calendar.lastSyncedAt
                      ? `Last updated: ${d(calendar.lastSyncedAt).format(
                          'D MMM, h:mm A'
                        )}`
                      : 'Not synced yet'}
                  </Typography.Text>
                </div>
                <div style={rowStyle}>
                  <Button
                    type="link"
                    style={actionBtnStyle}
                    icon={<SyncOutlined spin={isThisRefreshing} />}
                    loading={isThisRefreshing}
                    disabled={isSyncing}
                    onClick={() => sync(calendar._id)}
                  >
                    Refresh
                  </Button>
                </div>
                <div style={rowStyle}>
                  <Button
                    type="link"
                    style={actionBtnStyle}
                    icon={<EditOutlined />}
                    onClick={() => setSelectedCalendar(calendar)}
                  >
                    Edit
                  </Button>
                </div>
              </div>
            );
          })
        )}

        <div style={cardStyle}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setIsAddOpen(true)}
            onKeyDown={e => e.key === 'Enter' && setIsAddOpen(true)}
            style={{ ...rowStyle, borderTop: 'none', cursor: 'pointer' }}
          >
            <span style={{ flex: 1, fontWeight: 500 }}>
              Link another calendar
            </span>
            <RightOutlined style={{ color: '#717171', fontSize: '12px' }} />
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={copyExportLink}
            onKeyDown={e => e.key === 'Enter' && copyExportLink()}
            style={{ ...rowStyle, cursor: 'pointer' }}
          >
            <span style={{ flex: 1, fontWeight: 500 }}>
              Export Loger calendar
            </span>
            <ExportOutlined style={{ color: '#717171', fontSize: '12px' }} />
          </div>
        </div>
      </div>
    </>
  );
}

export default CalendarSyncPanel;
